import * as React from "react";
import currencyCodes from "currency-codes";
import {
    Form as FForm,
    Field as FField,
    useFormState,
} from "react-final-form";
import {
    Autocomplete,
    Button,
    Checkbox,
    Collapse,
    createFilterOptions,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField as MUITextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FORM_ERROR } from "final-form";
import ErrorLabel from "./Error";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { DISPLAY_DATE_FORMAT } from "./DataTable";
import DateRangePicker, { MODE_RANGE, MODE_UP_TO } from "./DateRangePicker";
import { API_DATE_FORMAT } from "../hooks/useLoadAllData";
import { useSelector } from "react-redux";
import { getSecurityName } from "../store/securities";
import { getExchangeName } from "../store/exchanges";
import { DateTime } from "luxon";
import FileUploader from "./FileUploader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAllCountries } from "../utils/countries";
import { twMerge } from "tailwind-merge";

/** Display label for a field - required fields are marked with an asterisk (same format as MUI's) */
function fieldLabel(field) {
    const label = field.label || field.name;
    return field.required ? `${label} *` : label;
}

/**
 * All different types of input fields
 */

function TextInput({ input, meta, field, displayError, ...rest }) {
    return (
        <MUITextField
            {...input}
            label={fieldLabel(field)}
            value={input.value}
            onChange={(e) => input.onChange(e.target.value)}
            helperText={displayError}
            {...rest}
            error={displayError ? true : false}
        />
    );
}

function CheckboxInput({ input, meta, field, displayError, className, ...rest }) {
    return (
        <div
            {...rest}
            className={twMerge(
                "flex flex-row items-center",
                className,
                field.allowUnset && ![true, false].includes(input.value)
                    ? "text-gray-500 line-through"
                    : "",
            )}
        >
            <FormControlLabel
                {...input}
                control={
                    <Checkbox
                        checked={input.value}
                        onChange={(e) => {
                            if (
                                field.allowUnset &&
                                input.value === false &&
                                e.target.checked
                            ) {
                                input.onChange(null);
                            } else {
                                input.onChange(e.target.checked);
                            }
                        }}
                    />
                }
                label={fieldLabel(field)}
            />
        </div>
    );
}

function DateInput({ input, meta, field, displayError, ...rest }) {
    const clearButton =
        field.allowEmpty && input.value ? (
            <InputAdornment position="end">
                <IconButton
                    size="small"
                    aria-label={`Clear ${field.label || field.name}`}
                    onClick={(e) => {
                        // Don't open the date picker when clearing
                        e.stopPropagation();
                        input.onChange(null);
                    }}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        ) : null;

    return (
        <MobileDatePicker
            {...input}
            label={fieldLabel(field)}
            value={input.value}
            onChange={(newValue) => {
                input.onChange(newValue);
            }}
            renderInput={(params) => (
                <TextInput
                    {...params}
                    field={field}
                    meta={meta}
                    input={input}
                    displayError={displayError}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: clearButton,
                    }}
                />
            )}
            ampm={false}
            inputFormat={DISPLAY_DATE_FORMAT}
            {...rest}
        />
    );
}

function DateRangeInput({ input, field, displayError, className }) {
    // Required date ranges must have both start + end dates - optional ones can be open-ended
    const modes =
        field.modes ||
        (field.required ? [MODE_RANGE] : [MODE_RANGE, MODE_UP_TO]);

    return (
        <DateRangePicker
            label={fieldLabel(field)}
            value={input.value || []}
            onChange={input.onChange}
            format={DISPLAY_DATE_FORMAT}
            modes={modes}
            className={className}
            error={displayError ? true : false}
            helperText={displayError}
        />
    );
}

function FileUploaderInput({ input, meta, field, displayError, ...rest }) {
    return (
        <>
            <div>{fieldLabel(field)}</div>
            <FileUploader
                {...input}
                onFileChosen={(filename, fileBody) => {
                    input.onChange({ filename, fileBody });
                }}
                {...rest}
                displayError={displayError}
            />
        </>
    );
}

function StringMapInput({ input, meta, field, displayError }) {
    const rows =
        input.value instanceof Array && input.value.length > 0
            ? input.value
            : [];

    const addRow = () => {
        input.onChange([...rows, { key: "", value: "" }]);
    };

    const removeRow = (index) => {
        input.onChange(rows.filter((_, i) => i !== index));
    };

    const updateRow = (index, col, val) => {
        input.onChange(
            rows.map((row, i) => (i === index ? { ...row, [col]: val } : row)),
        );
    };

    const keyTitle = field.keyTitle || "Key";
    const valueTitle = field.valueTitle || "Value";
    const keyWidthPercent = field.keyWidthPercent ?? 50;
    const rowGridStyle = {
        display: "grid",
        gridTemplateColumns: `${keyWidthPercent}fr ${100 - keyWidthPercent
            }fr auto`,
        gap: "8px",
        alignItems: "center",
        width: "100%",
    };

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="text-sm font-medium text-gray-700">
                {fieldLabel(field)}
            </div>
            {rows.length > 0 && (
                <div style={rowGridStyle}>
                    <div className="text-xs font-medium text-gray-500">
                        {keyTitle}
                    </div>
                    <div className="text-xs font-medium text-gray-500">
                        {valueTitle}
                    </div>
                    <div />
                </div>
            )}
            {rows.map((row, index) => (
                <div key={index} style={rowGridStyle}>
                    <MUITextField
                        size="small"
                        value={row.key}
                        onChange={(e) =>
                            updateRow(index, "key", e.target.value)
                        }
                    />
                    <MUITextField
                        size="small"
                        value={row.value}
                        onChange={(e) =>
                            updateRow(index, "value", e.target.value)
                        }
                    />
                    <Button
                        size="small"
                        color="error"
                        onClick={() => removeRow(index)}
                    >
                        Remove
                    </Button>
                </div>
            ))}
            <Button
                size="small"
                variant="outlined"
                onClick={addRow}
                sx={{ width: "fit-content" }}
            >
                Add row
            </Button>
            {displayError && (
                <div className="text-red-500 text-xs">{displayError}</div>
            )}
        </div>
    );
}

function SelectInput({ input, meta, field, displayError, multiple, ...rest }) {
    const optionsCb =
        field.options ||
        field.type.options ||
        field.optionsFromSelector ||
        field.type.optionsFromSelector;
    const optionsFromSelector = useSelector(
        optionsCb instanceof Function ? optionsCb : () => { },
    );
    // Callback function to generate options (e.g. used when generating options from managed securities)
    const options =
        optionsCb instanceof Function ? optionsFromSelector : optionsCb;

    // In case of default/initial value - we convert it from ID only, to an {id: x, label: y} object
    const convertValue = useCallback(
        (v) => {
            let value =
                v instanceof Object ? v : options.find((x) => x.id === v);
            if (value === undefined) value = null;
            return value;
        },
        [options],
    );

    // Convert any string IDs in the initial value to {id, label} objects after mount.
    // This must be done in useEffect (not during render) so that for multiple-select the
    // entire converted array is written in one onChange call rather than one call per element,
    // which would otherwise overwrite the field value with a single object and clear the selection.
    useEffect(() => {
        if (multiple) {
            if (
                input.value instanceof Array &&
                input.value.some((v) => !(v instanceof Object))
            ) {
                input.onChange(input.value.map(convertValue).filter(Boolean));
            }
        } else if (input.value && !(input.value instanceof Object)) {
            const converted = convertValue(input.value);
            if (converted) input.onChange(converted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount

    const multiValue = useMemo(
        () =>
            input.value instanceof Array && input.value.length > 0
                ? input.value.map(convertValue)
                : [],
        [input.value, convertValue],
    );
    let autocompleteValue;

    if (multiple) {
        // Multiple choice list - an array of values
        autocompleteValue = multiValue;
    } else {
        autocompleteValue = convertValue(input.value);
    }

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const filterOptions = createFilterOptions({
        limit: 40,
    });

    return (
        <Autocomplete
            {...input}
            multiple={multiple}
            filterOptions={filterOptions}
            options={options}
            renderInput={(params) => (
                <MUITextField
                    {...params}
                    label={fieldLabel(field)}
                    helperText={displayError}
                    error={displayError ? true : false}
                />
            )}
            renderOption={(props, option, { selected }) =>
                multiple ? (
                    <li {...props} key={option.id}>
                        <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                        />
                        {option.label}
                    </li>
                ) : (
                    <li {...props} key={option.id}>
                        {option.label}
                    </li>
                )
            }
            value={autocompleteValue}
            onChange={(e, value) => input.onChange(value)}
            isOptionEqualToValue={(option, value) =>
                value === undefined || value === "" || option.id === value.id
            }
            {...rest}
        />
    );
}

/**
 * All different types of fields. Each field has the following properties:
 *      component: input component to be used for display
 *      validate: callback function that validates input (returns an Error instance with error message if not validated), and will
 *              return the parsed value (e.g. convert string to float).
 *      parse: callback function that parses initial default value and returns a value that will be fed into the input
 *          component (e.g. converting epoch Unix timestamp into a DateTime object to be used by DatePicker component).
 *          If this function is not provided, default value will be passed as-is to the component.
 *      multipleValues: if true, `validate` returns an object of several values (instead of a single value), which
 *          will be submitted as separate keys (e.g. start + end dates of a date range).
 */

// Base field
const BaseField = {
    component: TextInput,
    validate: (x) => x,
    parse: (x) => x,
};

export const FileField = {
    ...BaseField,
    component: FileUploaderInput,
};

export const StringMapField = {
    ...BaseField,
    component: StringMapInput,
    validate: (x, field) =>
        Object.fromEntries(
            (x || []).map(({ key, value }) => [
                field.parseKey ? field.parseKey(key) : key,
                field.parseValue ? field.parseValue(value) : value,
            ]),
        ),
    parse: (x, field) =>
        x
            ? Object.entries(x).map(([key, value]) => ({
                key: field.formatKey ? field.formatKey(key) : String(key),
                value: field.formatValue
                    ? field.formatValue(value)
                    : String(value),
            }))
            : [],
};

export const TextField = {
    ...BaseField,
};

export const IntField = {
    ...BaseField,
    validate: (x) => {
        if (!/\d+/.test(x)) return new Error("Not a valid whole number");
        return parseInt(x);
    },
};

export const FloatField = {
    ...BaseField,
    validate: (x) => {
        if (!/^\d+\.?\d*$/.test(x)) return new Error("Not a valid number");
        return parseFloat(x);
    },
};

export const DecimalField = {
    ...BaseField,
    validate: (x) => {
        if (!/^\d+\.?\d*$/.test(x)) return new Error("Not a valid number");
        return x; // Decimal fields are saved as strings (to not lose accuracy)
    },
};

export const BooleanField = {
    ...BaseField,
    component: CheckboxInput,
};

export const DateField = {
    ...BaseField,
    component: DateInput,
    validate: (x) => x.toFormat(API_DATE_FORMAT),
    parse: (x) => (x ? DateTime.fromMillis(x) : null), // Convert from Unix epoch timestamp
};

/**
 * A single input for a date range - submitted as two separate values, under `fromName` and `toName`
 * (default: "start_date" and "end_date"). Default value is an array of [from, to] Unix epoch timestamps.
 * If not required, the range can be open-ended (only a start date or only an end date).
 */
export const DateRangeField = {
    ...BaseField,
    component: DateRangeInput,
    multipleValues: true,
    validate: ([from, to], field) => {
        if (field.required && (!from || !to)) {
            return new Error("Both start and end dates are required");
        }
        const format = (x) => (x ? x.toFormat(API_DATE_FORMAT) : null);
        return {
            [field.fromName || "start_date"]: format(from),
            [field.toName || "end_date"]: format(to),
        };
    },
    parse: (x) => (x ? x.map((v) => (v ? DateTime.fromMillis(v) : null)) : []), // Convert from Unix epoch timestamps
};

export const SelectField = {
    ...BaseField,
    component: SelectInput,
    validate: (x) => x.id,
};

export const MultipleSelectField = {
    ...BaseField,
    component: (props) => <SelectInput multiple {...props} />,
    validate: (x) => x.map((v) => v.id),
};

export const CurrencyField = {
    ...SelectField,
    options: currencyCodes.codes().map((c) => ({ id: c, label: c })),
};

export const MultipleCurrenciesField = {
    ...CurrencyField,
    ...MultipleSelectField,
};

export const CountryField = {
    ...SelectField,
    options: getAllCountries().map((c) => ({ id: c.iso2, label: c.country })),
};

export const MultipleCountriesField = {
    ...CountryField,
    ...MultipleSelectField,
};

export const SecurityField = {
    ...SelectField,
    optionsFromSelector: (state) =>
        state.securities.managedSecurities.map((s) => ({
            id: s.internal_id,
            label: getSecurityName(s),
        })),
};

export const MultipleSecuritiesField = {
    ...SecurityField,
    ...MultipleSelectField,
};

export const ExchangeField = {
    ...SelectField,
    optionsFromSelector: (state) =>
        state.exchanges.managedExchanges.map((e) => ({
            id: e._id,
            label: getExchangeName(e),
        })),
};

export const MultipleExchangesField = {
    ...ExchangeField,
    ...MultipleSelectField,
};

export const IndexField = {
    ...SelectField,
    optionsFromSelector: (state) =>
        state.indexes.managedIndexes.map((index) => ({
            id: index._id,
            label: `${index.internal_id} - ${index.name}`,
        })),
};

export const MultipleIndexesField = {
    ...IndexField,
    ...MultipleSelectField,
};

export const SourceField = {
    ...SelectField,
    optionsFromSelector: (state) =>
        state.securities.managedSources.map((e) => ({
            id: e._id,
            label: e.name,
        })),
};

export const MultipleSourcesField = {
    ...SourceField,
    ...MultipleSelectField,
};

// In case field type is not explicitly mentioned
const DEFAULT_FIELD_TYPE = TextField;

/** Is the field mandatory - `required` is either a boolean or a function of the current (raw) form values */
function isFieldRequired(field, values) {
    return typeof field.required === "function"
        ? field.required(values)
        : field.required;
}

/** Is the field hidden - `hidden` is either a boolean or a function of the current (raw) form values */
function isFieldHidden(field, values) {
    return typeof field.hidden === "function"
        ? field.hidden(values)
        : field.hidden;
}

/** Renders the form field - field must be one of IntField, TextField, etc. */
function Field({ field }) {
    const fieldType = field.type || DEFAULT_FIELD_TYPE;
    // Only re-render on form value changes if the field's `required` depends on them
    const { values } = useFormState({
        subscription: { values: typeof field.required === "function" },
    });

    // Resolve whether the field is currently mandatory - input components mark it with an asterisk (see `fieldLabel`)
    const displayField = {
        ...field,
        required: isFieldRequired(field, values),
    };

    return (
        <FField
            field={displayField}
            name={field.name}
            className={field.fullWidth ? "w-full" : "w-[300px]"}
            parse={(value) => (value === "" ? "" : value)}
            render={({ meta, ...rest }) => {
                // Determine if we should display an error for this field
                const displayError =
                    meta.invalid && !meta.modifiedSinceLastSubmit
                        ? meta.submitError
                        : null;

                // Render the field type's appropriate UI input component
                return fieldType.component({
                    displayError,
                    field: displayField,
                    meta,
                    ...rest,
                });
            }}
            placeholder={field.placeholder}
        />
    );
}

/** Our own custom form.
 *
 * @param fields Array of fields to display, each field is an object of:
 *      {
 *          "name": name as it will appear in values when submitted,
 *          "label": display label/title (UI),
 *          "defaultValue": default field value,
 *          "type": field type (e.g. `IntField`) - this will be used for the UI component to display, input validation,
 *                  conversion of input (e.g. text to float), etc.,
 *          "required": if true, mandatory field - can also be a function that receives the current (raw, unparsed)
 *              form values and returns whether the field is mandatory (e.g. depends on another field's value),
 *          "allowEmpty": if true, null values are allowed and will be saved
 *          "allowUnset": if true, will allow unsetting the value for this field, but unlike allowEmpty, this value
 *              will NOT be sent forward to the API at all, in case it's unset (currently implemented for BooleanField)
 *          "hidden": if true, the field is neither displayed nor submitted - can also be a function that receives the
 *              current (raw, unparsed) form values and returns whether the field is hidden,
 *          "collapsed": if true, the field is displayed in a "more fields" section, which is closed by default
 *      }
 * @param onSubmit (async function) called when the form is submitted - at this stage, all values (passed as an object)
 *      are assumed to be validated correctly and converted to the appropriate types (e.g. string -> float).
 *      If onSubmit fails, it throws an exception, which will be rendered as a general form error. Otherwise,
 *      it assumes it was successful.
 * @param submitButtonText Text on the form submit button
 * @param submitButtonColor (optional, default="primary") MUI palette color of the submit button (e.g. "warning")
 * @param submitButtonIcon (optional) icon element displayed to the left of the submit button's text
 * @param resetButtonText Text on the form reset button
 * @param additionalButtons list of additional buttons to display, each item in the list is an object of:
 *          { "text": button text, onPress: called when button is pressed }
 * @param additionalSubmitButtons list of additional submit buttons to display (next to the main submit button) - each
 *      validates the form like the main submit button, but calls its own submit callback, each item in the list is
 *      an object of: { "text": button text, onSubmit: (async function) same as `onSubmit`, called instead of it,
 *      "color": (optional, default="primary") MUI palette color of the button,
 *      "icon": (optional) icon element displayed to the left of the button's text }
 * @param hideResetButton (optional, default=False) if True, reset button will be hidden
 * @param moreFieldsButtonText Text on the button that opens/closes the section of collapsed fields
 * @param buttonsPosition (optional, default="bottom") "bottom" - buttons are displayed in a row below the fields,
 *      "right" - buttons are stacked in a column to the right of the fields (e.g. for wide forms)
 */
export default function Form({
    fields,
    onSubmit,
    submitButtonText = "Submit",
    submitButtonColor = "primary",
    submitButtonIcon = null,
    resetButtonText = "Reset",
    additionalButtons = [],
    additionalSubmitButtons = [],
    hideResetButton = false,
    className = "",
    buttonsClassName = "",
    moreFieldsButtonText = "More fields",
    buttonsPosition = "bottom",
}) {
    // Submit callback of the additional submit button that was pressed (null => main submit button / Enter key)
    const submitHandlerRef = useRef(null);
    const [activeSubmitButtonText, setActiveSubmitButtonText] = useState(null);
    const [showCollapsedFields, setShowCollapsedFields] = useState(false);

    const initialValues = useMemo(() =>
        Object.fromEntries(
            fields.map((f) => [
                f.name,
                (f.type || DEFAULT_FIELD_TYPE).parse(f.defaultValue, f),
            ]),
        ), []);


    /** Validate values, convert them if needed (e.g. string -> float) and call callback function */
    const onPreSubmit = async (values) => {
        // Pick the callback of the button that submitted the form, and reset it for the next submission
        const submitHandler = submitHandlerRef.current || onSubmit;
        submitHandlerRef.current = null;

        // Hidden fields aren't submitted
        const submittedFields = fields.filter(
            (field) => !isFieldHidden(field, values),
        );

        // Validate values and convert to proper types (e.g. string -> float)
        let parsedValues = submittedFields.map((field) => {
            const fieldType = field.type || DEFAULT_FIELD_TYPE;
            const fieldValue = values[field.name];
            const isRequired = isFieldRequired(field, values);
            const isEmptyValue =
                fieldValue === null ||
                fieldValue === "" ||
                fieldValue === undefined ||
                fieldValue.length === 0;

            let parsedFieldValue;

            if (!isRequired && isEmptyValue) {
                // Allow empty values (null)
                if (field.type === TextField && fieldValue === "") {
                    // Is an optional empty text field (e.g. someone editing a CA note and clearing it)
                    parsedFieldValue = "";
                } else if (field.allowEmpty && Array.isArray(fieldValue)) {
                    // Empty array on an allowEmpty field — validate it so the field is sent as [] not dropped
                    parsedFieldValue = fieldType.validate(fieldValue, field);
                } else {
                    parsedFieldValue = null;
                }
            } else if (isRequired && isEmptyValue) {
                // Empty field value, but it's a mandatory field
                parsedFieldValue = new Error("Mandatory field");
            } else {
                // Parse/convert the value
                parsedFieldValue = fieldType.validate(fieldValue, field);
            }

            return [field, parsedFieldValue];
        });
        const errorValues = parsedValues.filter(([_, v]) => v instanceof Error);

        if (errorValues.length > 0) {
            // Some validation errors were found - return those errors
            setActiveSubmitButtonText(null);
            // Make sure errors of collapsed fields are visible
            if (errorValues.some(([f]) => f.collapsed)) {
                setShowCollapsedFields(true);
            }
            return Object.fromEntries(
                errorValues.map(([f, v]) => [f.name, v.message]),
            );
        }

        // Filter out any null values (can happen with optional fields)
        // Do allow for null values in case of fields with allowEmpty == true
        parsedValues = parsedValues.filter(
            ([f, v]) => v !== null || f.allowEmpty,
        );

        // Field types with multiple values (e.g. date range) are submitted as separate keys - empty values
        // are dropped, unless allowEmpty == true
        const submittedEntries = parsedValues.flatMap(([f, v]) =>
            (f.type || DEFAULT_FIELD_TYPE).multipleValues
                ? Object.entries(v || {}).filter(
                    ([_, x]) => x !== null || f.allowEmpty,
                )
                : [[f.name, v]],
        );

        // Validation checked out - pass the converted/parsed values to the callback function
        try {
            await submitHandler(Object.fromEntries(submittedEntries));
        } catch (exc) {
            // Return a general submission error
            console.error(exc);
            const message = exc.response?.data?.Message || exc.message;
            return { [FORM_ERROR]: message };
        } finally {
            setActiveSubmitButtonText(null);
        }

        // If we've reached here, this means form submission was successful, nothing to do here
    };

    return (
        <FForm
            initialValues={initialValues}
            onSubmit={onPreSubmit}
            render={({
                handleSubmit,
                form,
                submitting,
                pristine,
                submitError,
                values,
            }) => {
                const fieldsClassName = twMerge(
                    "flex flex-row flex-wrap gap-y-4 gap-x-4",
                    className,
                );
                const visibleFields = fields.filter(
                    (f) => !isFieldHidden(f, values),
                );
                const collapsedFields = visibleFields.filter(
                    (f) => f.collapsed,
                );
                const renderField = (field) => (
                    <div
                        key={field.name}
                        className={twMerge(
                            "flex items-center",
                            field.fullWidth && "w-full",
                        )}
                    >
                        <Field field={field} />
                    </div>
                );

                const buttonsOnRight = buttonsPosition === "right";
                // Stacked buttons are spaced by their container's gap
                const buttonSx = buttonsOnRight ? {} : { mr: 2 };

                const fieldsContent = (
                    <>
                        {visibleFields
                            .filter((f) => !f.collapsed)
                            .map(renderField)}
                        {collapsedFields.length > 0 && (
                            <>
                                {/* Full-width wrapper - keeps the button on its own line when fields wrap into columns */}
                                <div className="w-full">
                                    <Button
                                        variant="text"
                                        onClick={() =>
                                            setShowCollapsedFields(
                                                (show) => !show,
                                            )
                                        }
                                        startIcon={
                                            showCollapsedFields ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )
                                        }
                                    >
                                        {moreFieldsButtonText}
                                    </Button>
                                </div>
                                {/* Collapsed fields must stay mounted (no `unmountOnExit`), so their inputs still
                                initialize their values (e.g. select inputs converting IDs to options) */}
                                <Collapse
                                    in={showCollapsedFields}
                                    className="w-full"
                                >
                                    <div className={fieldsClassName}>
                                        {collapsedFields.map(renderField)}
                                    </div>
                                </Collapse>
                            </>
                        )}
                        {submitError && (
                            <div className="w-full">
                                <ErrorLabel>Error: {submitError}</ErrorLabel>
                            </div>
                        )}
                    </>
                );

                const buttons = (
                    <div
                        className={twMerge(
                            "flex flex-row items-center",
                            buttonsOnRight &&
                            "flex-col items-stretch gap-y-3 shrink-0",
                            buttonsClassName,
                        )}
                    >
                        <LoadingButton
                            loading={
                                submitting &&
                                activeSubmitButtonText === null
                            }
                            disabled={submitting}
                            type="submit"
                            variant="contained"
                            color={submitButtonColor}
                            startIcon={submitButtonIcon}
                            sx={buttonSx}
                        >
                            {submitButtonText}
                        </LoadingButton>
                        {additionalSubmitButtons.map((button) => (
                            <LoadingButton
                                key={button.text}
                                loading={
                                    submitting &&
                                    activeSubmitButtonText === button.text
                                }
                                disabled={submitting}
                                type="button"
                                variant="contained"
                                color={button.color || "primary"}
                                startIcon={button.icon}
                                onClick={() => {
                                    submitHandlerRef.current =
                                        button.onSubmit;
                                    setActiveSubmitButtonText(button.text);
                                    form.submit();
                                }}
                                sx={buttonSx}
                            >
                                {button.text}
                            </LoadingButton>
                        ))}
                        {!hideResetButton && (
                            <Button
                                variant="outlined"
                                onClick={form.reset}
                                disabled={submitting || pristine}
                                sx={buttonSx}
                            >
                                {resetButtonText}
                            </Button>
                        )}
                        {additionalButtons.map((button) => (
                            <Button
                                key={button.text}
                                variant="outlined"
                                onClick={button.onPress}
                                sx={buttonSx}
                            >
                                {button.text}
                            </Button>
                        ))}
                    </div>
                );

                return buttonsOnRight ? (
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-row items-start gap-x-8"
                    >
                        <div className={twMerge(fieldsClassName, "flex-1")}>
                            {fieldsContent}
                        </div>
                        {buttons}
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} className={fieldsClassName}>
                        {fieldsContent}
                        {buttons}
                    </form>
                );
            }}
        />
    );
}
